import type { DetectedBlocksResult } from "./detectBlocks";
import {
  OFFICIAL_NEUROGRES_SKILLS,
  normalizeSkillKey,
  type NeurogresSkill,
} from "./skillGameMap";

type SkillRule = {
  key: string;
  pattern?: RegExp;
  when?: (context: InferSkillsContext) => boolean;
  skills: NeurogresSkill[];
};

export interface InferSkillsContext {
  detectedBlocks?: Partial<DetectedBlocksResult> | null;
  summaryText?: string | null;
}

function normalizeText(value = ""): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function collectText(context: InferSkillsContext): string {
  const detected = context.detectedBlocks || {};
  return normalizeText([
    context.summaryText,
    (detected.matches || []).map((entry) => entry?.matchedText).join(" "),
    (detected.matchedBlocks || []).join(" "),
    (detected.disqualifiers || []).map((entry) => entry?.matchedText).join(" "),
    (detected.stoppingQuestions || []).map((entry) => entry?.matchedText).join(" "),
    (detected.egoVictimPatterns || []).map((entry) => entry?.matchedText).join(" "),
    (detected.metaModelSignals || []).map((entry) => entry?.matchedText).join(" "),
  ].filter(Boolean).join(" "));
}

function dedupeSkills(skills: Array<NeurogresSkill | null | undefined>): NeurogresSkill[] {
  const unique = Array.from(new Set(skills.map((skill) => normalizeSkillKey(skill)).filter(Boolean)));
  return unique.filter((skill): skill is NeurogresSkill => OFFICIAL_NEUROGRES_SKILLS.includes(skill as NeurogresSkill));
}

const RULES: readonly SkillRule[] = [
  {
    key: "avoidance_and_impossibility",
    pattern: /\b(i can'?t|i cannot|it won'?t work|it will not work|avoid|avoidance|put it off|procrastinat|won't start)\b/i,
    skills: ["Persistence", "FlexibleThinking"],
  },
  {
    key: "overwhelm_and_scatter",
    pattern: /\b(overwhelm|overwhelmed|confused|confusion|scattered|all over the place|too many things|messy|chaotic)\b/i,
    skills: ["Planning", "TaskManagement"],
  },
  {
    key: "deadline_panic",
    pattern: /\b(deadline|rushed|panic|under pressure|time pressure|running out of time|last minute)\b/i,
    skills: ["DealingWithTimePressure", "TimeManagement"],
  },
  {
    key: "stuck_on_options",
    pattern: /\b(stuck on options|too many options|can'?t choose|cannot choose|can'?t see a way|cannot see a way|no possibility)\b/i,
    skills: ["IdentifyingOpportunities", "SystemicVision"],
  },
  {
    key: "change_resistance",
    pattern: /\b(change|changed|different|new way|adapt|adaptation|resist|resistance)\b/i,
    skills: ["CopingWithChanges", "FlexibleThinking"],
  },
  {
    key: "resource_constraints",
    pattern: /\b(bottleneck|capacity|constrained|limited resources|not enough time|not enough people|not enough support)\b/i,
    skills: ["ResourceManagement"],
  },
  {
    key: "over_analysis",
    pattern: /\b(over-analys|overanalys|too much analysis|logic breakdown|can't think clearly|cannot think clearly|missing anchors|how do you know)\b/i,
    skills: ["AnalysisAndDeduction", "LogicalThinking"],
  },
  {
    key: "hard_block_bias",
    when: ({ detectedBlocks }) => Boolean(detectedBlocks?.disqualifiers?.length || detectedBlocks?.stoppingQuestions?.length),
    skills: ["FlexibleThinking"],
  },
  {
    key: "ego_pattern_bias",
    when: ({ detectedBlocks }) => Boolean(detectedBlocks?.egoVictimPatterns?.length),
    skills: ["TakingInitiative", "DevelopingAPlan"],
  },
  {
    key: "meta_model_bias",
    when: ({ detectedBlocks }) => Boolean(detectedBlocks?.metaModelSignals?.length),
    skills: ["AnalysisAndDeduction"],
  },
];

export function inferSkillsFromBlocks(context: InferSkillsContext): NeurogresSkill[] {
  const text = collectText(context);
  const detected = context.detectedBlocks || {};
  const directSkills = dedupeSkills((detected.matches || []).map((entry) => entry?.skill || null));
  const ruleSkills = RULES.flatMap((rule) => {
    const matchedByPattern = rule.pattern ? rule.pattern.test(text) : false;
    const matchedByPredicate = typeof rule.when === "function" ? rule.when(context) : false;
    return (matchedByPattern || matchedByPredicate) ? rule.skills : [];
  });
  const allSkills = dedupeSkills([...directSkills, ...ruleSkills]);
  return allSkills.slice(0, 5);
}
